import { Component, Input, SimpleChanges, OnInit, OnChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BaseAvatarSize } from '../../../../shared/components/avatar/base-avatar.component';
import {
  InquiryResponse,
  InquiryCreateRequest,
  ShiftTimeRequest,
  ShiftTimeResponse,
  InquiryDateResponse,
} from '../../../../../api';
import { baseInquiryActions, BaseCoreState } from '../../../../_store';
import { BaseInquiryService } from '../../_services/base-inquiry.service';
import { WeekdaysArray } from '../../../../core/constants/base-calendar-values';
import { BaseTextareaComponent } from '../../../../shared/components/textarea/base-textarea.component';
import { BaseShiftIcons } from '../../_enums/base-inquiry.enum';
import { BaseInquiryType } from '../../_interfaces/base-inquiry.interface';

@Component({
  selector: 'base-inquiry-details-sidebar-content',
  templateUrl: './base-inquiry-details-sidebar-content.component.html',
})
export class BaseInquiryDetailsSidebarContentComponent implements OnInit, OnChanges {
  @ViewChild('jobDescriptionInput') jobDescriptionInput: BaseTextareaComponent;

  @Input() inquiry: BaseInquiryType | undefined;
  @Input() inquiryId: number | undefined;
  @Input() sidebarContent: TemplateRef<any>;
  @Input() isSidebarVisible = false;
  @Input() shiftTimes: (ShiftTimeResponse & { index: number; label?: string })[] = [];

  submitted = false;
  isEditNote = false;
  jobDescription = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  });
  savedJobDescriptionValue: string;
  weekdaysArray = WeekdaysArray;
  inquiryWeekdays: {
    mo: boolean;
    tu: boolean;
    we: boolean;
    th: boolean;
    fr: boolean;
    sa: boolean;
    su: boolean;
  };

  avatarSize = BaseAvatarSize;
  baseShiftIconsEnum = BaseShiftIcons;
  existingShifts = { fd: false, nd: false, zd: false, sd: false };

  constructor(private store: Store<BaseCoreState>, private baseInquiryService: BaseInquiryService) {}

  ngOnInit() {
    if (this.inquiry?.jobDescription) {
      this.jobDescription.setValue(this.inquiry.jobDescription);
      this.savedJobDescriptionValue = this.inquiry.jobDescription;
    }
    this.inquiryWeekdays = this.baseInquiryService.getInquiryWeekdays(this.inquiry?.dates || []);
    this.existingShifts = this.checkShifts(this.inquiry?.dates || []);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.isSidebarVisible) {
      const isVisible = changes.isSidebarVisible?.currentValue;

      if (!isVisible && this.isEditNote) {
        this.cancelJobDescriptionUpdate();
      }
    }
  }

  cancelJobDescriptionUpdate() {
    this.isEditNote = false;
    this.submitted = false;
    this.jobDescription.setValue(this.savedJobDescriptionValue);
  }

  editOrSaveJobDescription() {
    if (this.isEditNote) {
      this.submitted = true;
      if (this.jobDescription.valid && this.inquiryId && this.inquiry) {
        this.store.dispatch(
          baseInquiryActions.updateCustomerInquiry({
            payload: {
              inquiry: this.inquiryId,
              body: this.collectCustomerInquiryData(this.inquiry),
            },
          })
        );
        this.isEditNote = false;
        this.submitted = false;
        this.savedJobDescriptionValue = this.jobDescription.getRawValue();
      }
    } else {
      this.isEditNote = !this.isEditNote;
      this.submitted = false;

      setTimeout(() => {
        this.jobDescriptionInput.focus();
      });
    }
  }

  private collectCustomerInquiryData(inquiry: InquiryResponse): InquiryCreateRequest {
    const body: InquiryCreateRequest = {
      dates: inquiry.dates || [],
      unlimited: !!inquiry.unlimited,
      customerDepartment: inquiry.customerDepartment?.id as number,
      mainQualification: inquiry.mainQualification?.id as number,
      responsible: inquiry.responsible?.id as number,
      jobDescription: this.jobDescription.getRawValue(),
      costUnit: inquiry.costUnit || null,
      workEquipmentCustomer: inquiry.workEquipmentCustomer || null,
      workEquipmentPsp: inquiry.workEquipmentPsp || null,
      shiftTimes: inquiry.shiftTimes as ShiftTimeRequest[],
    };

    if (inquiry.qualifications) {
      body.qualifications = inquiry.qualifications.map((item) => item.id);
    }

    if (inquiry.requirements) {
      body.requirements = this.baseInquiryService.ungroupRequirements(inquiry.requirements).map((item) => item.id);
    }

    if (inquiry.weeklyWorkingHours) {
      body.weeklyWorkingHours = inquiry.weeklyWorkingHours;
    }

    return body;
  }

  private checkShifts(dates: InquiryDateResponse[]) {
    let shiftFd = false,
      shiftNd = false,
      shiftZd = false,
      shiftSd = false;

    for (const date of dates) {
      if (!shiftFd && (date.shiftFd ?? 0) > 0) {
        shiftFd = true;
      }
      if (!shiftNd && (date.shiftNd ?? 0) > 0) {
        shiftNd = true;
      }
      if (!shiftZd && (date.shiftZd ?? 0) > 0) {
        shiftZd = true;
      }
      if (!shiftSd && (date.shiftSd ?? 0) > 0) {
        shiftSd = true;
      }

      if (shiftFd && shiftNd && shiftZd && shiftSd) {
        break;
      }
    }

    return { fd: shiftFd, nd: shiftNd, zd: shiftZd, sd: shiftSd };
  }
}
